import * as THREE from 'three';
import { checkerboardCombination } from '@/components/CustomCheckerboard/MainViews/SizeSelector/combinations';
import geometryUpdate from '@/mixins/geometryUpdates';
import canvasUtils from "@/plugins/canvas/CanvasUtils";
import { getLocale } from "@/data/regionInfo";
import { external3DScene } from '@/mixins/external3DScene';

const cloneDeep = require('lodash/cloneDeep');

export default {
  data() {
    return {
      previewImageIsReady: false,
      isApplyingUGC: false,
      compositeImgKey: null,
      imagesPreview: null
    }
  },
  mixins: [geometryUpdate, external3DScene],
  computed: {
    showSpinner(){
      return !this.previewImageIsReady || this.isApplyingUGC;
    },
    pdm() {
      return this.$store.state.utils.pdm;
    },
    checkerboarderActive() {
      return this.$store.state.utils.queryParams.feature === 'checkerboarder';
    },
    activeUGCIndex() {
      return this.$store.state.ugc.tempData.activeUGCIndex;
    },
    storeMultipleUGCsizes() {
      return this.$store.state.ugc.tempData.features[this.activeUGCIndex].storeMultipleUGCsizes
    },
    activeComponent() {
      const isApparel = this.$store.getters['utils/isApparelEnabled'];
      if (isApparel){
        return this.$store.state.standardNavigation.activeSubComponent !== '' ?
          `${this.$store.state.standardNavigation.activeComponent}/${this.$store.state.standardNavigation.activeSubComponent}` :
          this.$store.state.standardNavigation.activeComponent;
      }
      else {
        return this.$store.state.legacyNavigation.activeComponent;
      }
    },
    recipe_v2() {
      return this.$store.state.recipe.recipe_v2;
    },
    defaultRecipe() {
      return this.$store.state.recipe.defaultRecipe;
    },
    selectedSwatches() {
      return this.$store.state.swatches.selectedSwatches;
    },
    tempData() {
      return this.$store.state.ugc.tempData.features[this.activeUGCIndex];
    },
    tempRecipe() {
      return this.$store.state.ugc.tempData.features[this.activeUGCIndex].recipe;
    },
    enableSnapShotService() {
      return this.$store.state.utils.configuration.enableSnapshotService;
    },
  },
  methods: {
    setIsApplyingUGC(isApplyingUGC = true) {
      this.isApplyingUGC = isApplyingUGC;
      this.$store.commit("ugc/stateManager/SET_LOADING", isApplyingUGC);
    },
    createCompositeImages(featuredView) {
      this.imagesPreview = {};
      const compositeImages = {};
      for (let i = 0; i < featuredView.length; i++) {
        const composite = [];
        if (featuredView[i].size?.includes("lg-")) {
          composite.push(featuredView[i]);
          if (featuredView[i].view?.includes("comp")) {
            i += 1;
            composite.push(featuredView[i]);
          }
          const compositeKey = `composite_${i}`;
          compositeImages[compositeKey] = composite;

          this.imagesPreview[compositeKey] = {
            active: false,
            caption: compositeKey,
            imgSrc: ''
          };
        }
      }
      return compositeImages;
    },
    processImages(data, featuredView) {
      const { width, height } = this.pdm.product_images.renditions.square;
      const compositeImages = this.createCompositeImages(featuredView);
      const compositeKeys = Object.keys(compositeImages);

      Promise.all(
        compositeKeys.map(key => {
          const images = [];
          // first image that will be rendered in the background layer
          const backView = compositeImages[key][0];
          images.push(
            canvasUtils.getImageFromUrl(
              data[`${backView.view}_view`][backView.size]
            )
          );
          // second image that will be rendered on top of background layer
          if (compositeImages[key].length > 1) {
            const frontView = compositeImages[key][1];
            images.push(
              canvasUtils.getImageFromUrl(
                data[`${frontView.view}_view`][frontView.size]
              )
            );
          }
          return Promise.all(images)
            .then(([imageBuffer, imageOverlay]) => {
              return canvasUtils.applyImageOverlay({
                imageBuffer,
                imageOverlay,
                width: width,
                height: height
              });
            })
            .then(imageBase64 => {
              if (this.imagesPreview) this.imagesPreview[key].imgSrc = imageBase64.src;
            });
        })
      ).then(() => {
        const firstKey = compositeKeys[0];
        this.compositeImgKey = firstKey;
        if (this.imagesPreview) this.imagesPreview[firstKey].active = true;
      });
    },
    async getUgcConfirmDesignImages() {
      const featuredViews = this.pdm.product_images.featured_views.ugc_confirm_design;
      const keyNames = Object.keys(featuredViews);
      if (!keyNames.length) {
        console.error('PDM has no featured_views for ugc_confirm_design');
        return;
      }
      const firstKey = keyNames[0];
      const featuredView = featuredViews[firstKey];
      const currentRecipe = this.$store.state.recipe.recipe_v2;
      const recipe = this.$store.getters["ugc/tempData/getRecipe"];
      if(this.enableSnapShotService) {
        await this.$store.dispatch("recipe/SAVE_RECIPE", {recipe});
        let imageData = {};
        for (const obj of featuredView) {
          const imageObj = await this.initExternal3Dscene({domEl: "snapshot-image-scene", snapshotSize: obj.size, snapshotView: obj.view, showSnapShot: false, applyUGC: true});
          this.disposeScene();
          imageData[`${obj.view}_view`] = {};
          imageData[`${obj.view}_view`][obj.size] = imageObj.fileUrl;
        }
        this.processImages(imageData, featuredView);
        this.$store.commit("recipe/SET_RECIPE_V2", currentRecipe);
        this.$store.commit("recipe/SET_RECIPE_ID", currentRecipe.recipe_id);
      } else {
        this.$store
          .dispatch("ugc/tempData/GET_PRODUCT_IMAGES_BY_RECIPE", {
            locale: getLocale(),
            recipe: recipe,
            images: featuredView
          })
          .then(data => {
            this.processImages(data, featuredView);
          })
          .catch(err => {
            this.$store.commit("utils/SET_ERROR_MODAL_PROCESS", true);
            console.error("Error getting product images by recipe:", err);
          });
        }
    },
    onPreviewImageLoaded() {
      this.previewImageIsReady = true;
      this.$store.commit("ugc/stateManager/SET_LOADING", false);
      this.$eventBus.$emit("disable-finish-button"); // TopBar, it ENABLES the finish button logic
      this.$nextTick(()=>{
        document.querySelector("#close-button-newUgcModal").focus();
      })
    },
    /**
     * @function clearUgcStorage
     *
     * Entry point to start the process of apply the ugc created image on the 3D model.
     * - If the user has selected a new size during the ugc flow, uses setModelBySize
     *    to change the size of the 3D model.
     * - Calls finishUGCFlow to continue the process of apply an ugc swatch.
     */
    clearUgcStorage() {
      // Deletes the ugcStorage if coming from the replace flow.
      // Replace does not support multiple recipe sizes in the storage.
      if (!this.storeMultipleUGCsizes) {
        this.$store.commit("ugc/storageController/CLEAR_RECIPES");
      }
    },
    /**
     * Checks if the the ugc_swatch for the second component should be deleted from patternMaker store
     * In authentic classic, there are restrictions with flames and drips in quarters component.
     */
    checkSecondaryPatternTransferDesign(activeComponent, secondaryComponent) {
      const currentSticker = this.$store.state.patternMaker.selectedSticker[secondaryComponent] || this.$store.state.patternMaker.selectedSticker[activeComponent];
      if (!!secondaryComponent && !!currentSticker) {
        const { available_stickers: availableStickers } = this.$store.state.utils.availablePatternMakerContent[this.pdm.sku]
        && this.$store.state.utils.availablePatternMakerContent[this.pdm.sku][secondaryComponent];
        return availableStickers.includes(Number(currentSticker));
      };
      return false;
    },
    /**
     * @function loadPrecreatedUGCRecipe
     * @param {string} componentName
     * Load ugc from a precreated recipe.
     */
    applyUGCFromPrecreatedRecipe(componentName) {
      const secondaryComponent = this.$store.getters["ugc/getUGCComponentNotActive"];
      const recipeObj = this.recipe_v2.components[componentName];
      if (this.$ugc.isUGCCreatedByFeature(recipeObj, { includeImageLibrary: true })) {
        // set image lib data
        const storageRecipe = this.$store.getters["imageGallery/getStorageRecipe"];
        if (this.$store.state.utils.isFinishMyDesignApplied || !storageRecipe) this.$store.commit("imageGallery/SET_LIBRARY_RECIPE", this.recipe_v2);
        const storageComponent = this.$store.getters["imageGallery/getStorageRecipeComponent"]({ activeComponent: componentName })
          || this.recipe_v2.components[componentName];
        const selectedCategory = storageComponent?.toolkit_options?.image_library_category;
        this.$store.commit("imageGallery/SET_IMAGE_CATEGORY", selectedCategory);
        this.$store.commit("imageGallery/SET_COMPONENT_CURRENT_IMAGE", {
          componentName,
          category: selectedCategory,
          ugcObject: storageComponent.ugc
        });
      }

      if (this.$ugc.isUGCCreatedByFeature(recipeObj, { includePatternMaker: true })) {
        const isTransferDesign = this.$store.state.transferDesign.isCustomizerTransferredModel;
        const isStickerApplicable = this.checkSecondaryPatternTransferDesign(componentName, secondaryComponent);
        if (secondaryComponent !== componentName && isTransferDesign && !isStickerApplicable) {
          this.$store.commit("patternMaker/RESET_PATTERN", secondaryComponent );
        }
        const ugcData = {
          ugc: recipeObj.ugc,
          swatch_id: recipeObj.swatch_id
        };
        this.$store.commit("patternMaker/SET_PATTERN", {
          data: ugcData,
          componentName
        });
      }
      if (this.$ugc.isUGCCreatedByFeature(recipeObj, { includeCustomCheckerboard: true })) {
        const { hex_1: color1, hex_2: color2, warp, size } = this.$recipe.extractToolkitOptions(componentName, checkerboardCombination);
        const canvasConfig = {
          color1,
          color2,
          warp,
          size,
        };
        this.$store.commit('customCheckerboard/SET_COMPONENT_CONFIG', {
          component: componentName,
          canvasConfig: canvasConfig,
          ugc: recipeObj.ugc
        });
        this.$store.commit("customCheckerboard/SET_CANVAS_CONFIG", canvasConfig);
        this.$store.commit("customCheckerboard/SET_TEMP_COMPONENTS");
      }
    },
    applySavePreviousUGCSwatch() {
      let saveComponents = [];
      const ugcComponents = this.tempData?.applyUGCComponents

      ugcComponents.forEach((component) => {
      const feature = this.$recipe.getUGCFeatureType(component);

        this.$store.commit('swatches/SET_SWATCHES_SELECTED', {
          component,
          data: { ...this.selectedSwatches[component], default: false },
        });
        saveComponents.push({ component, feature, isSameFeature: feature === 'ugc' });
      });

      if (this.$store.state.utils.configuration.enableFlyOutMenu) {
        this.updateStandardMenuForUGC();
      }

      this.savePreviousSwatch({ saveComponents });
    },
    /**
     * @function finishUGCFlow
     * Second step on the process of applying ugc, This method saves the data coming from the ugc flow into rhe recipe_v2
     * And creates the 3D textures to be applied to the 3D model
     *
     */
    finishUGCFlow(loadingPrecreatedRecipe) {
      const isImgLibFlow = this.$store.state.ugc.tempData.features[1].continueAndEditFlow || this.activeUGCIndex === 0;
      if(!this.$store.state.utils.isFinishMyDesignApplied && isImgLibFlow) this.applySavePreviousUGCSwatch();
      return new Promise((resolve,reject) => {
        this.createAllUGCTextures(loadingPrecreatedRecipe).then(() => {
          if (loadingPrecreatedRecipe) {
            const ugcSize = this.tempData.ugc_size;

            if (ugcSize) {
              this.$store.commit("ugc/tempData/SET_BUCKET_SIZE", this.$store.state.utils.bucketSizes[ugcSize]);
            }
          } else if(this.tempData.tempBucketSize) {
            this.$store.commit("ugc/tempData/SET_BUCKET_SIZE", this.tempData.tempBucketSize);
            this.$store.commit("ugc/tempData/SET_TEMP_BUCKET_SIZE", null);
          }

          if (this.storeMultipleUGCsizes) {
            // Open buy panel if coming from the replace flow
            this.$eventBus.$emit("show-buy-panel");
            this.$eventBus.$emit("set-ugc-size-selected");
          }
          if (this.tempData.deletePreviousRecipe && this.tempData.deletePreviousRecipe !== this.tempData.bucketSize) {
            this.$store.commit("ugc/storageController/DELETE_RECIPE", this.tempData.deletePreviousRecipe);
          }
          if (this.$store.getters["utils/isGCC"]) {
            this.$store.commit("ugc/tempData/SET_ACTIVE_UGC_COMPONENT", this.activeComponent);
          }
          // Run The UGC Feature Geometry Updates
          this.resetGeometrybyUGCFeature();
          this.$store.commit("ugc/tempData/HAS_FINISHED_APPLYING", true);
          resolve();
        }).catch(reject);
      });
    },
    /**
     * Checks if feature from URL params OR swatch sobgroup match an availableUgcFeature
     *
     * Applies the Geometry Updates
     */
    applyUGCFeatureGeometry() {
      const featureId = this.$store.state.utils.queryParams.feature;
      let activeComponent = this.activeComponent;
      if (this.activeComponent.search('/') !== -1) {
         activeComponent = this.activeComponent.split('/')?.[0];
      }
      let recipeComponent = this.recipe_v2.components[activeComponent];
      let isValidFeatureGeoUpdate = this.isValidFeatureGeoUpdate({componentName: activeComponent, feature: recipeComponent.subgroup});
      if ((featureId && recipeComponent.ugc && recipeComponent.subgroup === featureId) || isValidFeatureGeoUpdate) {
        if(!featureId) this.$store.commit("ugc/tempData/SET_UGC_FEATURE", recipeComponent.subgroup);
        this.updateGeometryPerUgcFeature({feature: featureId || recipeComponent.subgroup, component: recipeComponent});
      }
    },
    /**
     * Get the Feature Geometry Key from geometry_updates PDM and apply them
     * @param {String} feature eg: "crayola"
     */
    updateGeometryPerUgcFeature({feature, component}) {
      let featureGeometryKey = this.getFeatureGeometryPdmKey({feature, searchType: "geometry_updates"});
      if (featureGeometryKey && featureGeometryKey.length > 0) {
        this.updateFeatureModelGeometry({ componentName: component, geometryUpdate: featureGeometryKey });
      }
    },
    updateStandardMenuForUGC() {
      const menu = cloneDeep(this.$store.state.standardNavigation.menu);

      this.tempData.applyUGCComponents.forEach((component) => {

        const currentNode = menu.find(node => node.id === component);

        if (currentNode) {
          currentNode.isChecked = !this.selectedSwatches[component]?.default;
        }
      });

      this.$store.commit('standardNavigation/UPDATE_MENU_STANDARD_NAVIGATION', { menu });
    },
    createAllUGCTextures(loadingPrecreatedRecipe) {
      const recipe = loadingPrecreatedRecipe ? this.recipe_v2 : this.tempRecipe;
      // get the ugc recipe data from temp data.
      let ugcUpdate = this.returnUGCUpdates(recipe);
      // update the size on the ugc as a swatch if they ahve been applied
      ugcUpdate = this.updateUgcSwatchSize(ugcUpdate);
      // saves the ugc data in the recipe.
      this.updateUGC(ugcUpdate);
      // Create 3D textures for each object in the ugc temp data.
      const promises = ugcUpdate.map(({ component, ugc }) =>
        this.getUGCSwatchMaterial(component, ugc.ugc_swatch)
          .then(textureObj => this.applyUGCSwatchToModel(component, textureObj))
      );

      return Promise.all(promises);
    },
    getUGCSwatchMaterial(componentName, ugcSwatch, saveToStore = true) {
      const { textureList, componentPDM, options } = this.getUGCSwatchTexture(componentName, ugcSwatch);
      const material_options = componentPDM.ugc.ugc_swatch.material_options;
      return this.$model3D.loadMultipleTextures(textureList)
        .then(loadedTextures => {
          material_options.mesh_options.color = new THREE.Color(this.$store.state.ugc.template_guide_bg);

          const material = this.$model3D.sceneHelpers.createMaterial({
            mesh_type: material_options.type,
            options: Object.assign({}, material_options.mesh_options, loadedTextures),
            componentName,
            shadersType: this.recipe_v2.components[componentName].ugc && componentPDM.ugc ?
              componentPDM.ugc.shader_material : componentPDM.shader_material
          });

          if (saveToStore && this.activeUGCIndex === 0) {
            this.$store.commit("ugc/SAVE_MATERIAL", { componentName, material });
          }

          this.$model3D.sceneHelpers.updateComponentStitching({ componentName });

          return material;
        });
    },
    /**
     * @function getUGCSwatchTexture
     * @param {string} - componentName - Name of the component to apply the ugc swatch
     * @param {Object} - ugcSwatch - UGC swatch created by the endpoint
     * @param {Object} - ugcSwatch - UGC swatch created by the endpoint
     *
     * Create the 3D texture for the UGC image with ThreeJS.
     */
    getUGCSwatchTexture(componentName, ugcSwatch) {
      const componentPDM = this.pdm.components[componentName];
      const merchandisingUGC = componentPDM.ugc;
      const material_options = { ...merchandisingUGC.ugc_swatch.material_options };
      const { s3Url } = this.$store.state.endPoints.customizer;
      const { enableS3Customizer } = this.$store.state.utils.configuration;
      const textureList = JSON.parse(
        JSON.stringify(material_options.textures)
      );

      textureList.map.image_url = ugcSwatch.image_url + "?timestamp=" + Math.round(new Date() / 1000);

      if(!/^((http|https):\/\/)/.test(textureList.map.image_url)) {
        textureList.map.image_url = !enableS3Customizer ? `${s3Url}${textureList.map.image_url}` : `${textureList.map.image_url}`;
      }

      textureList.normalMap.image_url = `${this.$store.getters['endPoints/swatchesPath']()}${textureList.normalMap.image_url}`

      return { textureList, componentPDM };
    },
    applyPatternUGCSwatch(componentName, type = "customizer", applyUGCMaterial = true, updatePrice = true) {
      if(!this.$store.state.patternMaker.currentPattern[componentName]?.ugc) return;
      const { ugc: { ugc_swatch } } = this.$store.state.patternMaker.currentPattern[componentName];

      if(!/^((http|https):\/\/)/.test(ugc_swatch.image_url )) {
        ugc_swatch.image_url = `${this.$store.state.endPoints[type].s3Url}${ugc_swatch.image_url}`;
      }

      const tempPattern = this.$store.state.patternMaker.currentPattern[componentName];
      // save patternMaker in recipe
      return this.$recipe.updatePattern(
        componentName,
        JSON.parse(JSON.stringify(tempPattern.ugc)),
        tempPattern.swatch_id,
        updatePrice,
      ).then(() => {
        if (applyUGCMaterial) return this.getUGCSwatchMaterial(componentName, ugc_swatch, false)
          .then(material => this.applyUGCMaterial(componentName, material));
        return;
      });
    },
    applyUGCMaterial(componentName, ugcMaterialObject) {
      const ugcComponentObject = this.$model3D.sceneHelpers.getSceneObjectByName({ objectName: componentName });
      this.$model3D.sceneHelpers.disposeMaterial({ material: ugcComponentObject.material });

      ugcComponentObject.material = ugcMaterialObject;

      this.$model3D.sceneHelpers.updateComponentStitching({ componentName});
    },
    /**
     * @function applyUGCSwatchToModel
     * @param {string} - componentName - Name of the component to apply the ugc swatch
     * @param {Object} - textureObj - 3D texture object to apply to the 3D model.
     * This method disposes the previous texture on the 3D model and applies the newly created ugc texture to the 3D model
     */
    applyUGCSwatchToModel(componentName, textureObj) {
      return new Promise((resolve) => {
        const tonalStitch = this.returnTonalStitch(componentName);
        //Dispose the old texture that the component had before applying ugc.
        this.$model3D.sceneHelpers.disposeMaterial({
          material: this.$model3D.sceneHelpers.getSceneObjectByName({ objectName: componentName }).material
        });
        this.$model3D.sceneHelpers.storeMaterial({ componentName, swatchId: -1 });
        this.$model3D.sceneHelpers.getSceneObjectByName({ objectName: componentName }).material = textureObj;
        // Apply stitches tonal color
        this.$model3D.sceneHelpers.updateComponentStitching({ componentName, color: tonalStitch });
        // deco stitch changes go here.
        this.$model3D.sceneHelpers.removeComponentFromData(componentName);
        resolve();
      })
    },
    returnTonalStitch(componentName) {
      let tonalStitch = null;
      const placements = this.$store.state.ugc.placements.responses;
      if (placements[componentName] && placements[componentName][0].tonal_stitch_color) {
        tonalStitch = placements[componentName][0].tonal_stitch_color;
        this.$store.commit("ugc/SAVE_TONAL_STITCH", {
          componentName,
          tonalStitch
        });

        if (!this.tempRecipe.components[componentName].ugc.tonal_stitch_color) {
          const newRecipe = {
            ...this.tempRecipe
          };
          tonalStitch = this.$store.state.ugc.tonalStitch[componentName];
          newRecipe.components[componentName].ugc.tonal_stitch_color = tonalStitch;
          this.$store.commit("ugc/tempData/SET_RECIPE", newRecipe);
        }
      } else {
        tonalStitch = this.recipe_v2.components[componentName].ugc.tonal_stitch_color;
      }
      return tonalStitch;
    },
    /**
     * @function returnUGCUpdates
     * @param {Object} - recipe - Full recipe_v2 object.
     * @return {Array} - ugcUpdates - Array with the ugc objects
     *
     * Loops through the recipe and looks for ugc objects in each component
     * Returns an array with said objects if found.
     */
    returnUGCUpdates(recipe) {
      const ugcUpdates = [];
      Object.keys(recipe.components).forEach(componentName => {
        if (recipe.components[componentName].ugc) {
          ugcUpdates.push({
            component: componentName,
            ugc: recipe.components[componentName].ugc,
            subgroup: recipe.components[componentName].subgroup,
          });
        }
      });
      return ugcUpdates;
    },
    /**
     * @function updateUgcSwatchSize
     * @param {Array} - ugcUpdates - Array with the ugc objects
     * @returns {Array} - ugcUpdatesData
     *
     * Loops through the ugcUpdates array and updates the size for each ugc object found
     */
    updateUgcSwatchSize(ugcUpdates) {
      const isApparel = this.$store.getters['utils/isApparelEnabled'];
      const { activeUGCComponent } = this.tempData;
      let ugcSize = "";
      if (isApparel)
        ugcSize = "0.0";
      else
        ugcSize = ugcUpdates.find(({ component }) => activeUGCComponent === component)?.ugc?.size
      const ugcSwatchComponents = this.getComponentsWithUgcApplied(true);
      const ugcUpdatesData = [];
      ugcUpdates.forEach(({ component, ugc, subgroup }) => {
        if (ugcSwatchComponents.includes(component)) {
          ugc.size = ugcSize;
        }
        ugcUpdatesData.push({ component, ugc, subgroup });
      });
      return ugcUpdatesData;
    },
    /**
     * @function updateUGC
     * @param {array} - ugcMap - object with the event of the data, including timestamp
     *
     * Updates the ugc object in the recipe for each componet found in ugcMap
     */
    updateUGC(ugcMap) {
      if (!ugcMap || !ugcMap.length) return;
      ugcMap.forEach(({ component: componentName, ugc, subgroup }) => {
        const recipe = this.tempData.recipe &&
          this.tempData.recipe.components &&
          this.tempData.recipe.components[componentName] ?
          this.tempData.recipe : this.recipe_v2;
        const tmpUgc = JSON.parse(JSON.stringify(ugc));
        // check that the update is not ugc as a swatch or patternMaker created
        const notUgcSwatch =
          !this.$ugc.isUGCCreatedByFeature(recipe.components[componentName], { includePatternMaker: true, includeCustomCheckerboard: true }) &&
          !this.$store.getters["swatches/isUGCFromSwatch"]({ swatchId: recipe.components[componentName].swatch_id });
        if (notUgcSwatch) {
          const group = recipe.components[componentName].group;
          if(group !== 'graphics' && subgroup.indexOf('graphics') < 0 ) {
            this.$recipe.updateComponents(componentName, this.defaultRecipe.components[componentName].swatch_id);
            this.$recipe.updateUGC(componentName, tmpUgc, subgroup);
          }
          const { tempData } = this.$store.state.ugc;
          if(tempData.activeUGCIndex === 0 && subgroup === 'ugc') {
            const bucketSize = tempData.features[0].tempBucketSize || this.returnBucketSize(componentName);
            this.$store.commit("ugc/storageController/STORE_UGC", {
              componentName,
              ugcObject: ugc,
              bucketSize
            });
          }
        }
        this.$eventBus.$emit("update-menu-item-state", componentName);
      });
      this.$store.dispatch("buyPanel/GET_PRICE", { recipe: this.recipe_v2})
    },
    /**
     * @function getComponentsWithUgcApplied
     * @param {boolean} - ugcSwatchCheck - include UGC as a swatch in the search
     * @returns {array} - Returns an array with the componets that have ugc applied
     */
    getComponentsWithUgcApplied(ugcSwatchCheck) {
      const ugcComponents = this.$store.getters["ugc/getUGCEnabledComponents"];
      const componentsWithUgcApplied = [];
      if (ugcComponents.length) {
        ugcComponents.forEach(component => {
          const componentHasUgc = this.$recipe.checkForUGCComponent(component);
          const componentRecipe = this.recipe_v2.components[component];
          let hasUgcSwatch =  false;
          if (componentRecipe)
            hasUgcSwatch= this.$store.getters["swatches/isUGCFromSwatch"]({swatchId: componentRecipe.swatch_id})
          if (!ugcSwatchCheck && (hasUgcSwatch || componentHasUgc)) {
            componentsWithUgcApplied.push(component);
            return;
          }
          // Gets components with regular ugc or swatch ugc depending on ugcSwatchCheck boolean
          if ((ugcSwatchCheck && hasUgcSwatch) || (!ugcSwatchCheck && !hasUgcSwatch && componentHasUgc)) {
            componentsWithUgcApplied.push(component);
          }
        });
      }
      return componentsWithUgcApplied;
    },
    /**
     * @function returnBucketSize
     * @return {string} - bucketSize - ex: '9.0'
     * Find the bucket size in any of the places it can be stored and return it.
     */
    returnBucketSize(componentName) {
      let bucketSize;
      const storeUnisexSizes = this.$store.state.utils.bucketSizes
      if (this.tempData && this.tempData.bucketSize) {
        bucketSize = this.tempData.bucketSize;
      } else if (storeUnisexSizes) {
        let ugcSize;
        if (this.$store.getters["ugc/storageController/IS_IN_STORAGE"](componentName)) {
          ugcSize = this.$store.getters["ugc/storageController/GET_SIZE"](componentName);
        } else {
          ugcSize = this.$recipe.getUGCSizeFromRecipe();
        }
        bucketSize = storeUnisexSizes[ugcSize];
      }
      // If no bucket size is found, return default value from PDM
      if (!bucketSize && this.$store.state.utils && this.$store.state.utils.pdm) {
        bucketSize = storeUnisexSizes[this.pdm.ugcDefaultSize];
      }

      return bucketSize;
    },
    reApplyUgcSwatch({ componentNameParam } = {}) {
      let { activeUGCComponent: componentName, ugcFeature =  null } = this.tempData;
      //this is for undo
      if(componentNameParam) componentName = componentNameParam;
      const { subgroup } = this.recipe_v2.components[componentName];
      const bucketSize = this.returnBucketSize(componentName);

      // update recipe
      let tempUGCObj = this.$store.state.ugc.storageController?.recipes?.[bucketSize]?.[componentName];
      tempUGCObj = !!tempUGCObj ? tempUGCObj : this.$store.state.imageGallery.storageController.recipe?.components?.[componentName]?.ugc;
      this.$recipe.updateComponents(componentName, this.defaultRecipe.components[componentName].swatch_id);
      this.$recipe.updateUGC(componentName, tempUGCObj);

      this.$store.commit("recipe/UPDATE_UGC", { componentName, ugcObj: tempUGCObj, subgroup: (ugcFeature || "ugc") });

      // apply deco stitch if found
      this.$eventBus.$emit('re-apply-decostitch');

      let featureIdQuery = "ugc";
      let featureIdBlock = "ugc";
      if (subgroup === "checkerboard-creator") {
        featureIdQuery = "checkerboard";
        featureIdBlock = "patternMaker";
      } else if (subgroup === 'library' && this.activeUGCIndex === 1) {
        featureIdQuery = "image_library";
        featureIdBlock = "imageLibrary";
      }

      // Disable the ugc swatches when ugc is applied
      this.$store.dispatch("swatches/GET_RELATION_SWATCH_BY_FEATURE_ID", featureIdQuery)
        .then(({ swatches_not_allowed }) => {
          this.$store.dispatch("buyPanel/GET_PRICE", { recipe: this.recipe_v2 })
          this.$store.commit("swatches/SET_DISABLED_SWATCHES_BY_FEATURE", { swatches: swatches_not_allowed, feature: featureIdBlock });
        });

      //Apply ugc texture to 3D model
      const existingMaterial = this.$model3D.sceneHelpers.getSceneObjectByName({ objectName: componentName}).material;
      const savedMesh = this.$store.state.ugc.savedUGCMesh[componentName];
      //remove material from previous swatch
      if (!savedMesh || (existingMaterial && existingMaterial.uuid !== savedMesh.uuid)) {
        this.$model3D.sceneHelpers.disposeMaterial({ material: existingMaterial });
      }

      // apply saved mesh to 3D component
      this.$model3D.sceneHelpers.getSceneObjectByName({ objectName: componentName }).material = savedMesh;
      // Apply stitches tonal color
      this.$model3D.sceneHelpers.updateComponentStitching({ componentName, color: this.returnTonalStitch(componentName) });

      // Run The UGC Feature Geometry Updates
      this.resetGeometrybyUGCFeature();
      this.applyUGCFeatureGeometry();
    },
    /**
     * @function changeDecostitchStitching
     * @param {string} - ugcEvent - string with the name of the event, ex: 'ugc click button'
     * @returns {object} - object with the event of the data, including timestamp
     *
     */
    getAnalyticsData(ugcEvent) {
      return {
        ugcStep: ugcEvent,
        timeStamp: new Date().toISOString(),
        event: "ugcStep",
        eventCategory:'customs',
        eventAction:'UGC',
        eventLabel: ugcEvent
      };
    },
    applyUGCAsSwatch({ component, ugc }) {
      this.updateUGC({ component, ugc });

      return this.getUGCSwatchMaterial(component, ugc.ugc_swatch, false)
        .then(textureObj => this.applyUGCSwatchToModel(component, textureObj));
    }
  }
};
